/* istanbul ignore file */
import React from 'react';
import { type CookieObj, acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { Seo } from 'src/components/atoms/Seo/Seo';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import { buildSitemapGroups } from 'src/services/contentstack/buildSitemapGroups';
import { fetchDepartmentPages } from 'src/services/contentstack/fetchDepartmentPages';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchAllProductListingPagesForSitemap } from 'src/services/contentstack/fetchProductListingPagesForSitemap';
import * as styles from 'src/styles/sitemap.styles';
import type { PageMetadata } from 'src/types/PageProps';
import { getPageMetadata } from 'src/utils/getPageMetadata';

interface SitemapPageProps {
  departmentGroups: ReturnType<typeof buildSitemapGroups>['departmentGroups'];
  otherPages: ReturnType<typeof buildSitemapGroups>['otherPages'];
  pageEventCategory: string;
  pageMetadata: PageMetadata;
  siteChromeContent: SiteChromeContent | null;
}

export const getServerSideProps: GetServerSideProps<SitemapPageProps> = async (context) => {
  const pageMetadata: PageMetadata = {
    ...getPageMetadata(context),
    functionalCookiesConsented: acceptsFunctionalCookies(context.req.cookies as CookieObj),
  };

  const [siteChromeContent, departments, plpItems] = await Promise.all([
    fetchGlobalContent({
      featureFlags: pageMetadata.featureFlags,
      isPreview: pageMetadata.isPreview,
    }).then(([chrome]) => chrome),
    fetchDepartmentPages(pageMetadata.isPreview),
    fetchAllProductListingPagesForSitemap(pageMetadata.isPreview),
  ]);

  const { departmentGroups, otherPages } = buildSitemapGroups(departments, plpItems);

  return {
    props: {
      departmentGroups,
      otherPages,
      pageMetadata,
      pageEventCategory: 'Sitemap',
      siteChromeContent,
    },
  };
};

const SitemapPage = ({
  departmentGroups,
  otherPages,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => (
  <div id={SKIP_CONTENT_TARGET_ID}>
    <Seo
      description="Browse department pages and product listing pages on notonthehighstreet.com."
      title="Sitemap | notonthehighstreet.com"
    />
    <main css={styles.main}>
      <h1 css={styles.pageTitle}>Sitemap</h1>

      {departmentGroups.map(({ department, productListingPages }) => (
        <section
          aria-labelledby={`dept-${department.url}`}
          css={styles.section}
          key={department.url}
        >
          <h2 css={styles.sectionTitle} id={`dept-${department.url}`}>
            <a href={department.url}>{department.title}</a>
          </h2>
          {productListingPages.length > 0 ? (
            <ul css={styles.linkGrid}>
              {productListingPages.map((plp) => (
                <li css={styles.linkGridItem} key={plp.url}>
                  <a href={plp.url}>{plp.heading}</a>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}

      {otherPages.length > 0 ? (
        <section aria-labelledby="sitemap-other" css={styles.section}>
          <h2 css={styles.sectionTitle} id="sitemap-other">
            Other pages
          </h2>
          <ul css={styles.linkGrid}>
            {otherPages.map((page) => (
              <li css={styles.linkGridItem} key={page.url}>
                <a href={page.url}>{page.heading}</a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </main>
  </div>
);

export default SitemapPage;
